/**
 * The twelve income categories — THE ONE PLACE for the whole application.
 *
 * The convention `expense-categories.ts` established in feature 009, applied to money coming in.
 * Read that file's header for the full argument; the short version is that this list is identical
 * for every household, no member may add to it, and nothing about it varies per household — so there
 * is nothing to seed. There are no category rows anywhere, and no per-household work when the list
 * changes.
 *
 * ADDING A THIRTEENTH CATEGORY
 *
 * Two changes, and no more: a line here, and `alter type income_category add value '…'` in a
 * migration. The enum stays because the database keeps its own guard against a value the domain
 * never validated. Nothing under `packages/domains/income` changes, and no household row is touched
 * — and there is a test that fails if a second hand-written copy of this list appears anywhere.
 *
 * WHY HOLIDAY ALLOWANCE AND 13TH MONTH ARE SEPARATE FROM BONUS
 *
 * A Dutch household treats vakantiegeld and a thirteenth month as two different, predictable things
 * arriving in two different months. Folded into `bonus` the chart could still show the spikes but
 * could not say which was which, and "why was May large" is exactly the question a month view is
 * opened to answer.
 */

/**
 * THE LABELS LEFT THIS FILE IN FEATURE 021, AND `value` STAYED.
 *
 * A label is a SENTENCE and `value` is an IDENTIFIER, so the two belong on opposite sides of that
 * feature's line. The kernel is the wrong side twice over: `apps/api` imports it and has no reader to
 * show a label to, and a Dutch member cannot read one written here.
 *
 * They live in `@gladis/ui/i18n`, keyed `category.income.<value>`, in both languages. NOTHING ELSE
 * MOVED — the Zod enum below still derives from `value`, the `income_category` database enum is
 * untouched, and every stored row means exactly what it meant.
 *
 * ADDING AN EIGHTEENTH CATEGORY IS STILL TWO CHANGES, as this file has always said. They are now a
 * line here plus a line in the migration, and a line in each of the two catalogue modules — which is
 * the same rule with the label counted where it actually lives. `catalogue-keys-resolve.test.ts`
 * fails if the two halves disagree.
 */
export const INCOME_CATEGORIES = [
  { value: "salary" },
  { value: "freelance" },
  { value: "holiday_allowance" },
  { value: "thirteenth_month" },
  { value: "bonus" },
  { value: "benefits_and_allowances" },
  { value: "pension" },
  { value: "investment_income" },
  { value: "rental" },
  { value: "tax_refund" },
  { value: "gift" },
  { value: "other" },
] as const;

/**
 * The category identifiers, exactly the `income_category` enum's values.
 *
 * Derived from the array rather than written a second time. A hand-maintained union here would be a
 * second copy of the list, which is the thing this module exists to prevent.
 */
export type IncomeCategoryValue = (typeof INCOME_CATEGORIES)[number]["value"];

export const INCOME_CATEGORY_VALUES: readonly IncomeCategoryValue[] = INCOME_CATEGORIES.map(
  (category) => category.value,
);
